import { useRef, useCallback, useEffect, useState } from 'react';
import {
	calculateCursor,
	getCycleAtPosition,
	applySnapToCyclePosition,
	determineMouseDownAction
} from '../services/interaction';
import { DEFAULT_CONFIG, CURSOR_TYPES, UPDATE_TYPE, MOUSE_ACTION_TYPE } from '../utils/constants';
import { timeToX, xToTime } from '../utils/coordinate-math';
import { DragEngine } from '../services/drag-engine';
import { useSnap } from './useSnap';
import { CycleData, InteractionType, UpdateType, ValidationError } from '../types';
import { isDuplicateCycleUpdate } from '../utils/helpers/cycle-comparison';

type SeekUpdateData = { time: number };
type CycleUpdateData = { cycleId: string; start: number; end: number };
type UpdateData = SeekUpdateData | CycleUpdateData;

type PendingUpdate =
	| { type: typeof UPDATE_TYPE.SEEK; data: SeekUpdateData }
	| { type: typeof UPDATE_TYPE.CYCLE; data: CycleUpdateData };

interface TimelineInteractionProps {
	cycles: CycleData[];
	videoDuration: number;
	onCycleChange: (cycleId: string, start: number, end: number) => void;
	onValidationError?: (error: ValidationError | null) => void;
	onCycleSelect: (cycleId: string | null) => void;
	canvasRef: React.RefObject<HTMLCanvasElement | null>;
	canvasDimensions?: { width: number; height: number };
	onEnterEditMode?: (cycleId: string) => void;
	onSeek?: (time: number) => void;
	currentTime?: number;
	isEditingIdentified?: boolean;
	isEditMode?: boolean;
	editingCycleId?: string | null;
	onHoverTimeChange?: (time: number, x: number, y: number) => void;
	onHoverEnd?: () => void;
	allowBilateral?: boolean;
	onScrubbingChange?: (isScrubbing: boolean) => void;
	onCycleDragChange?: (isDragging: boolean) => void;
	onPauseVideo?: () => void;
	allCycles?: CycleData[];
	allowOverlap?: boolean;
}

export function useTimelineInteraction({
	cycles,
	videoDuration,
	onCycleChange,
	onValidationError,
	onCycleSelect,
	canvasRef,
	canvasDimensions,
	onSeek,
	currentTime = 0,
	isEditingIdentified = false,
	isEditMode = false,
	editingCycleId,
	onHoverTimeChange,
	onHoverEnd,
	allowBilateral = true,
	onScrubbingChange,
	onCycleDragChange,
	onPauseVideo,
	allCycles,
	allowOverlap = false
}: TimelineInteractionProps) {
	const dragEngineRef = useRef<DragEngine | null>(null);
	const isDraggingTimeNeedleRef = useRef<boolean>(false);
	const isDraggingCycleRef = useRef<boolean>(false);
	const isScrubbingRef = useRef<boolean>(false);
	const lastMouseXRef = useRef<number>(0);
	const globalMouseMoveHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
	const globalMouseUpHandlerRef = useRef<(() => void) | null>(null);

	const rafIdRef = useRef<number | null>(null);
	const pendingUpdateRef = useRef<PendingUpdate | null>(null);

	const lastCommittedUpdateRef = useRef<{
		cycleId: string;
		start: number;
		end: number;
	} | null>(null);

	const [snapTime, setSnapTime] = useState<number | null>(null);

	const [dragPreview, setDragPreview] = useState<{
		cycleId: string;
		start: number;
		end: number;
		isValid: boolean;
	} | null>(null);

	const [hoveredCycleId, setHoveredCycleId] = useState<string | null>(null);

	const [lastDragValidation, setLastDragValidation] = useState<boolean>(true);
	const lastValidPositionRef = useRef<{ start: number; end: number } | null>(null);
	const lastReportedDragErrorKeyRef = useRef<string | null>(null);
	const onValidationErrorRef = useRef(onValidationError);
	onValidationErrorRef.current = onValidationError;

	const onCycleDragChangeRef = useRef(onCycleDragChange);
	onCycleDragChangeRef.current = onCycleDragChange;

	const inferErrorType = useCallback((message: string): ValidationError['type'] => {
		const normalizedMessage = message.toLowerCase();

		if (normalizedMessage.includes('duration') || normalizedMessage.includes('dura')) {
			return 'duration';
		}

		if (normalizedMessage.includes('bound') || normalizedMessage.includes('range')) {
			return 'bounds';
		}

		return 'overlap';
	}, []);

	const { snapTime: snap } = useSnap(cycles, currentTime, videoDuration);

	const getCanvasWidth = useCallback(() => {
		const canvas = canvasRef.current;
		return canvasDimensions?.width || canvas?.offsetWidth || 0;
	}, [canvasRef, canvasDimensions]);

	const processPendingUpdate = useCallback(() => {
		if (!pendingUpdateRef.current) return;

		const update = pendingUpdateRef.current;
		pendingUpdateRef.current = null;
		rafIdRef.current = null;

		if (update.type === UPDATE_TYPE.SEEK) {
			onSeek?.(update.data.time);
			return;
		}

		if (update.type === UPDATE_TYPE.CYCLE) {
			const { cycleId, start, end } = update.data;

			const lastUpdate = lastCommittedUpdateRef.current;

			if (isDuplicateCycleUpdate(lastUpdate, cycleId, start, end)) {
				return;
			}

			lastCommittedUpdateRef.current = { cycleId, start, end };

			onCycleChange(cycleId, start, end);
		}
	}, [onSeek, onCycleChange]);

	const scheduleUpdate = useCallback(
		(type: UpdateType, data: UpdateData) => {
			pendingUpdateRef.current = { type, data } as PendingUpdate;

			if (rafIdRef.current === null) {
				rafIdRef.current = requestAnimationFrame(processPendingUpdate);
			}
		},
		[processPendingUpdate]
	);

	useEffect(() => {
		const canvasWidth = getCanvasWidth();
		dragEngineRef.current = new DragEngine({
			cycles,
			videoDuration,
			canvasWidth,
			allowBilateral,
			isEditingIdentified,
			editingCycleId,
			allCycles,
			allowOverlap
		});
	}, []);

	useEffect(() => {
		const canvasWidth = getCanvasWidth();
		dragEngineRef.current?.updateConfig({
			cycles,
			videoDuration,
			canvasWidth,
			allowBilateral,
			isEditingIdentified,
			editingCycleId,
			allCycles,
			allowOverlap
		});
	}, [
		cycles,
		videoDuration,
		currentTime,
		getCanvasWidth,
		allowBilateral,
		isEditingIdentified,
		editingCycleId,
		allCycles,
		allowOverlap
	]);

	const isNearTimeNeedle = useCallback(
		(mouseX: number, mouseY: number, canvasWidth: number): boolean => {
			const margin = DEFAULT_CONFIG.MARGIN;
			const needleX = timeToX(currentTime, videoDuration, canvasWidth, margin);
			const thresholdX = 20;
			const isNearX = Math.abs(mouseX - needleX) <= thresholdX;
			const isInNeedleHandleArea = mouseY <= DEFAULT_CONFIG.NEEDLE_HANDLE_HEIGHT;
			return isNearX && isInNeedleHandleArea;
		},
		[currentTime, videoDuration]
	);

	const startScrubbing = useCallback(() => {
		isScrubbingRef.current = true;
		onScrubbingChange?.(true);
		onPauseVideo?.();

		if (globalMouseMoveHandlerRef.current) {
			document.addEventListener('mousemove', globalMouseMoveHandlerRef.current);
		}
		if (globalMouseUpHandlerRef.current) {
			document.addEventListener('mouseup', globalMouseUpHandlerRef.current);
		}
	}, [onScrubbingChange, onPauseVideo]);

	const endScrubbing = useCallback(() => {
		if (isScrubbingRef.current) {
			isScrubbingRef.current = false;
			onScrubbingChange?.(false);
		}
		setSnapTime(null);
	}, [onScrubbingChange]);

	const getMousePos = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement> | MouseEvent): number => {
			const canvas = canvasRef.current;
			if (!canvas) return 0;
			const canvasLeftInViewport = canvas.getBoundingClientRect().left;
			return e.clientX - canvasLeftInViewport;
		},
		[canvasRef]
	);

	const getMousePosY = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement> | MouseEvent): number => {
			const canvas = canvasRef.current;
			if (!canvas) return 0;
			const canvasTopInViewport = canvas.getBoundingClientRect().top;
			return e.clientY - canvasTopInViewport;
		},
		[canvasRef]
	);

	const getCanvasHeight = useCallback(() => {
		const canvas = canvasRef.current;
		return canvasDimensions?.height || canvas?.offsetHeight || 0;
	}, [canvasRef, canvasDimensions]);

	const updateCursorForDragState = useCallback(() => {
		if (isDraggingCycleRef.current) {
			if (!lastDragValidation) {
				document.body.style.cursor = CURSOR_TYPES.NOT_ALLOWED;
				return;
			}

			const interactionType = dragEngineRef.current?.getInteractionType();
			const isResizing =
				interactionType === InteractionType.START_RESIZE || interactionType === InteractionType.END_RESIZE;
			document.body.style.cursor = isResizing ? CURSOR_TYPES.EW_RESIZE : CURSOR_TYPES.GRABBING;
			return;
		}

		if (isScrubbingRef.current || isDraggingTimeNeedleRef.current) {
			document.body.style.cursor = CURSOR_TYPES.EW_RESIZE;
		}
	}, [lastDragValidation]);

	const processTimeNeedleDrag = useCallback(
		(mouseX: number, canvasWidth: number, canvasTopInViewport: number, clientX: number) => {
			const margin = DEFAULT_CONFIG.MARGIN;
			const newTime = xToTime(mouseX, videoDuration, canvasWidth, margin);
			scheduleUpdate(UPDATE_TYPE.SEEK, { time: newTime });
			onHoverTimeChange?.(newTime, clientX, canvasTopInViewport);
		},
		[videoDuration, scheduleUpdate, onHoverTimeChange]
	);

	const processCycleDrag = useCallback(
		(mouseX: number) => {
			if (!dragEngineRef.current?.isDragging()) return;

			const result = dragEngineRef.current.calculateDragPosition(mouseX);

			if (!result) {
				onHoverEnd?.();
				setSnapTime(null);
				setDragPreview(null);
				setLastDragValidation(true);
				return;
			}

			const interactionType = dragEngineRef.current.getInteractionType();

			const snapResult = applySnapToCyclePosition(result.start, result.end, result.isValid, interactionType);

			setSnapTime(snapResult.snapIndicatorTime);
			setLastDragValidation(result.isValid);

			if (result.isValid) {
				if (lastReportedDragErrorKeyRef.current !== null) {
					lastReportedDragErrorKeyRef.current = null;
					onValidationErrorRef.current?.(null);
				}

				lastValidPositionRef.current = {
					start: snapResult.finalStart,
					end: snapResult.finalEnd
				};

				scheduleUpdate(UPDATE_TYPE.CYCLE, {
					cycleId: result.cycleId,
					start: snapResult.finalStart,
					end: snapResult.finalEnd
				});
				onSeek?.(snapResult.finalStart);
			} else if (result.error) {
				const errorKey = `${result.cycleId}:${result.error}`;

				if (lastReportedDragErrorKeyRef.current !== errorKey) {
					lastReportedDragErrorKeyRef.current = errorKey;
					onValidationErrorRef.current?.({
						type: inferErrorType(result.error),
						cycleId: result.cycleId,
						message: result.error,
						severity: 'error'
					});
				}
			}

			setDragPreview({
				cycleId: result.cycleId,
				start: snapResult.finalStart,
				end: snapResult.finalEnd,
				isValid: result.isValid
			});

			onHoverEnd?.();
		},
		[onHoverEnd, getCanvasWidth, scheduleUpdate, onSeek, snap, inferErrorType]
	);

	const cleanupDragListeners = useCallback(() => {
		if (globalMouseMoveHandlerRef.current) {
			document.removeEventListener('mousemove', globalMouseMoveHandlerRef.current);
		}
		if (globalMouseUpHandlerRef.current) {
			document.removeEventListener('mouseup', globalMouseUpHandlerRef.current);
		}
	}, []);

	const resetDragState = useCallback(() => {
		const wasDraggingCycle = isDraggingCycleRef.current;

		dragEngineRef.current?.endDrag();
		isDraggingTimeNeedleRef.current = false;
		isDraggingCycleRef.current = false;

		if (wasDraggingCycle) {
			onCycleDragChange?.(false);
		}

		endScrubbing();
		setSnapTime(null);
		setDragPreview(null);
		setLastDragValidation(true);
		lastValidPositionRef.current = null;
		lastCommittedUpdateRef.current = null;
		lastReportedDragErrorKeyRef.current = null;
		document.body.style.cursor = '';
	}, [endScrubbing, onCycleDragChange]);

	const startCycleDrag = useCallback(() => {
		isDraggingCycleRef.current = true;
		onCycleDragChange?.(true);
		lastValidPositionRef.current = null;
		if (globalMouseMoveHandlerRef.current) {
			document.addEventListener('mousemove', globalMouseMoveHandlerRef.current);
		}
		if (globalMouseUpHandlerRef.current) {
			document.addEventListener('mouseup', globalMouseUpHandlerRef.current);
		}
	}, [onCycleDragChange]);

	useEffect(() => {
		globalMouseMoveHandlerRef.current = (e: MouseEvent) => {
			const canvas = canvasRef.current;
			if (!canvas) return;

			updateCursorForDragState();

			const canvasRect = canvas.getBoundingClientRect();
			const mouseX = e.clientX - canvasRect.left;
			const canvasWidth = canvasDimensions?.width || canvas.offsetWidth || 0;

			lastMouseXRef.current = mouseX;

			if (isScrubbingRef.current || isDraggingTimeNeedleRef.current) {
				processTimeNeedleDrag(mouseX, canvasWidth, canvasRect.top, e.clientX);
				return;
			}

			if (isDraggingCycleRef.current) {
				processCycleDrag(mouseX);
			}
		};

		globalMouseUpHandlerRef.current = () => {
			const wasScrubbing = isScrubbingRef.current;
			const wasDraggingCycle = isDraggingCycleRef.current;

			if (wasScrubbing && !wasDraggingCycle) {
				onPauseVideo?.();
			}

			resetDragState();
			cleanupDragListeners();
		};
	}, [
		canvasRef,
		canvasDimensions,
		updateCursorForDragState,
		processTimeNeedleDrag,
		processCycleDrag,
		resetDragState,
		cleanupDragListeners,
		cycles,
		videoDuration,
		onCycleSelect,
		onSeek,
		onPauseVideo
	]);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement>) => {
			const canvas = canvasRef.current;
			if (!canvas) return;

			const mouseX = getMousePos(e);
			const mouseY = getMousePosY(e);
			const canvasWidth = getCanvasWidth();
			const canvasHeight = getCanvasHeight();
			const isInNeedleHandleArea = mouseY <= DEFAULT_CONFIG.NEEDLE_HANDLE_HEIGHT;
			const isInCycleBarArea = mouseY >= canvasHeight - DEFAULT_CONFIG.CYCLE_BAR_HEIGHT;

			const interaction = dragEngineRef.current?.startDrag(mouseX) || null;
			const isNearNeedle = isNearTimeNeedle(mouseX, mouseY, canvasWidth);
			const hasUnidentifiedCycle = cycles.some((c) => !c.identified);

			const action = determineMouseDownAction(
				interaction,
				isInNeedleHandleArea,
				isInCycleBarArea,
				isNearNeedle,
				hasUnidentifiedCycle
			);

			if (action.shouldPreventDefault) {
				e.preventDefault();
				e.stopPropagation();
			}

			if (action.type === MOUSE_ACTION_TYPE.CYCLE_DRAG) {
				if (action.cycleId) {
					const clickedCycle = cycles.find((c) => c.id === action.cycleId);

					onCycleSelect(action.cycleId);

					if (clickedCycle && onSeek) {
						onSeek(clickedCycle.start);
					}
				}
				startCycleDrag();
				return;
			}

			if (action.type === MOUSE_ACTION_TYPE.NEEDLE_SCRUB) {
				startScrubbing();
				return;
			}

			if (action.type === MOUSE_ACTION_TYPE.CYCLE_SELECT) {
				const clickedCycle = cycles.find((c) => c.id === action.cycleId);

				if (clickedCycle) {
					onCycleSelect(action.cycleId!);
					onSeek?.(clickedCycle.start);
				}
				return;
			}

			if (action.type === MOUSE_ACTION_TYPE.EMPTY_SCRUB) {
				startScrubbing();

				const margin = DEFAULT_CONFIG.MARGIN;
				const clickedTime = xToTime(mouseX, videoDuration, canvasWidth, margin);
				scheduleUpdate(UPDATE_TYPE.SEEK, { time: clickedTime });

				if (!hasUnidentifiedCycle) {
					onCycleSelect(null);
				}
			}
		},
		[
			canvasRef,
			getMousePos,
			getMousePosY,
			getCanvasWidth,
			getCanvasHeight,
			isNearTimeNeedle,
			startScrubbing,
			startCycleDrag,
			onCycleSelect,
			cycles,
			onSeek,
			videoDuration,
			scheduleUpdate
		]
	);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement>) => {
			const canvas = canvasRef.current;
			if (!canvas) return;

			const mouseX = getMousePos(e);
			const canvasWidth = getCanvasWidth();
			const margin = DEFAULT_CONFIG.MARGIN;
			const canvasTopInViewport = canvas.getBoundingClientRect().top;

			if (isScrubbingRef.current || isDraggingTimeNeedleRef.current) {
				const newTime = xToTime(mouseX, videoDuration, canvasWidth, margin);
				scheduleUpdate(UPDATE_TYPE.SEEK, { time: newTime });
				onHoverTimeChange?.(newTime, e.clientX, canvasTopInViewport);

				return;
			}

			if (dragEngineRef.current?.isDragging()) {
				processCycleDrag(mouseX);
				return;
			}

			const hoveredTime = xToTime(mouseX, videoDuration, canvasWidth, margin);
			onHoverTimeChange?.(hoveredTime, e.clientX, canvasTopInViewport);

			const mouseY = getMousePosY(e);
			const canvasHeight = getCanvasHeight();
			const cycleBarHeight = DEFAULT_CONFIG.CYCLE_BAR_HEIGHT;

			if (isNearTimeNeedle(mouseX, mouseY, canvasWidth)) {
				canvas.style.cursor = CURSOR_TYPES.POINTER;
				setHoveredCycleId(null);
				return;
			}

			const cycleAtMouse = getCycleAtPosition(
				mouseX,
				mouseY,
				cycles,
				canvasWidth,
				canvasHeight,
				videoDuration,
				cycleBarHeight
			);
			setHoveredCycleId(cycleAtMouse?.id || null);

			const cursorResult = calculateCursor(
				mouseX,
				mouseY,
				cycles,
				canvasWidth,
				videoDuration,
				editingCycleId,
				isEditMode
			);
			canvas.style.cursor = cursorResult.cursor;
		},
		[
			cycles,
			videoDuration,
			onCycleChange,
			onSeek,
			onHoverTimeChange,
			onHoverEnd,
			getMousePos,
			getMousePosY,
			getCanvasWidth,
			getCanvasHeight,
			canvasRef,
			editingCycleId,
			isNearTimeNeedle,
			scheduleUpdate
		]
	);

	const handleMouseUp = useCallback(() => {
		dragEngineRef.current?.endDrag();
		isDraggingTimeNeedleRef.current = false;
		endScrubbing();
	}, [endScrubbing]);

	const handleMouseLeave = useCallback(() => {
		onHoverEnd?.();
		setHoveredCycleId(null);
	}, [onHoverEnd]);

	useEffect(() => {
		return () => {
			if (isDraggingCycleRef.current) {
				onCycleDragChangeRef.current?.(false);
			}

			if (rafIdRef.current !== null) {
				cancelAnimationFrame(rafIdRef.current);
				rafIdRef.current = null;
			}

			document.body.style.cursor = '';
			if (globalMouseMoveHandlerRef.current) {
				document.removeEventListener('mousemove', globalMouseMoveHandlerRef.current);
			}
			if (globalMouseUpHandlerRef.current) {
				document.removeEventListener('mouseup', globalMouseUpHandlerRef.current);
			}
		};
	}, []);

	return { handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave, snapTime, dragPreview, hoveredCycleId };
}
